import React, { useEffect, useState } from 'react';
import { Modal, Select, Typography, Alert, message } from 'antd';
import { useApiRequest } from '../../hooks/useApiRequest';

const { Text } = Typography;

const ROLE_LABELS = {
    lead_advisor: 'Lead Advisor',
    advisor: 'Advisor',
    company_spoc: 'Company SPOC',
    company_delegate: 'Company Delegate',
};

/**
 * Enrol project members in a PUBLISHED course (POST .../training/enrollments). The API
 * skips non-members and already-enrolled users and reports each skip, which this modal
 * surfaces rather than hiding. Advisor / Lead Advisor only (server-enforced).
 */
const EnrollModal = ({ open, projectId, course, members, onClose, onDone }) => {
    const { request } = useApiRequest();
    const [userIds, setUserIds] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [skipped, setSkipped] = useState([]);

    useEffect(() => {
        if (open) {
            setUserIds([]);
            setError(null);
            setSkipped([]);
        }
    }, [open]);

    const handleOk = async () => {
        if (userIds.length === 0) return setError('Choose at least one learner.');
        setBusy(true);
        setError(null);
        try {
            const { data } = await request('POST', `/projects/${projectId}/training/enrollments`, {
                body: { courseId: course.id, userIds },
            });
            const created = (data.enrollments || []).length;
            const skips = data.skipped || [];
            if (created > 0) message.success(`${created} learner(s) enrolled.`);
            if (skips.length > 0) {
                setSkipped(skips);
                if (created === 0) setError('No one was enrolled - see the reasons below.');
                else onDone();
                return;
            }
            onDone();
        } catch (err) {
            setError(err.message || 'Could not enrol the selected learners.');
        } finally {
            setBusy(false);
        }
    };

    const nameOf = (id) => (members || []).find((m) => m.id === id)?.name || id;

    return (
        <Modal
            open={open}
            title={course ? `Enrol learners - ${course.title} (v${course.version})` : 'Enrol learners'}
            okText="Enrol"
            confirmLoading={busy}
            onOk={handleOk}
            onCancel={onClose}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                    <Text strong>Learners</Text>
                    <Select
                        aria-label="Learners"
                        mode="multiple"
                        style={{ width: '100%' }}
                        value={userIds}
                        onChange={setUserIds}
                        optionFilterProp="label"
                        placeholder="Select project members"
                        options={(members || []).map((m) => ({
                            value: m.id,
                            label: `${m.name} (${ROLE_LABELS[m.role] || m.role})`,
                        }))}
                    />
                </div>
                {skipped.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Some learners were skipped"
                        description={skipped.map((s) => `${nameOf(s.userId)}: ${s.reason}`).join(' · ')}
                    />
                )}
                {error && <Text type="danger" role="alert">{error}</Text>}
            </div>
        </Modal>
    );
};

export default EnrollModal;
